import './ExpirationDatePicker.scss';
import { autobind, BaseComponent, IBaseProps } from 'office-ui-fabric-react/lib/Utilities';
import { DatePicker as BaseDatePicker, IDatePicker, IDatePickerProps } from 'office-ui-fabric-react/lib/DatePicker';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { months, days, shortDays, shortMonths } from '../../../utilities/ShareHelper';
import {
  ClientId
} from '../../../interfaces/SharingInterfaces';
import stringsResx from '../../Share.resx';
import * as React from 'react';
import { TooltipHost, DirectionalHint } from 'office-ui-fabric-react/lib/Tooltip';
import * as StringHelper from '@ms/odsp-utilities/lib/string/StringHelper';
import * as PropTypes from 'prop-types';
import PlatformDetection from '@ms/odsp-utilities/lib/browser/PlatformDetection';
import * as ClientIdHelper from '../../../utilities/ClientIdHelper';
import { ICalloutProps } from 'office-ui-fabric-react/lib/Callout';

export type IPartialCalloutProps = Pick<ICalloutProps, 'preventDismissOnScroll'>;

const DatePicker: React.ComponentType<IDatePickerProps & { showCloseButton?: boolean, allFocusable?: boolean, closeButtonAriaLabel?: string, calloutProps?: IPartialCalloutProps, tabIndex?: number }> = BaseDatePicker;

export interface IExpirationDatePickerProps extends IBaseProps {
  expiryRestriction: number; // The maximum number of days an anonymous link can be active before expiring.
  onSelectDate: (date: Date) => void;
  value: Date;
  onUpdatePassword?: () => void;
  clientId: ClientId;
  disableAutoFocus?: boolean; // Prevents focus from automatically opening the picker
  hideRemoveDateButton?: boolean;
  showGleam?: boolean;
  tabIndex?: number;
}

export interface IExpirationDatePickerState {
  hasExpired: boolean;
  selectedDate: Date | null;
}

export class ExpirationDatePicker extends BaseComponent<IExpirationDatePickerProps, IExpirationDatePickerState> {
  private _datePickerStrings;
  private _minDate: Date;
  private _maxDate: Date;
  // Tracks when the expiration date is removed for screen reader users
  private _expirationDateRemoved: boolean;
  private _platformDetection: PlatformDetection;
  private _datePicker: IDatePicker;
  private _isMobile: boolean;
  static contextTypes = {
    platformDetection: PropTypes.object.isRequired
  };
  // Tracks whether a specific date has been set yet
  constructor(props: IExpirationDatePickerProps, context: any) {
    super(props);
    const isExpired = this.props.value < new Date(Date.now());
    // Instantiate state without an existing link template
    this.state = {
      hasExpired: isExpired,
      selectedDate: null
    };

    this._platformDetection = context.platformDetection;
    const now: Date = new Date(Date.now());
    this._minDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
    if (props.expiryRestriction > 0) {
      this._maxDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + props.expiryRestriction, now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
    } else {
      // default to a max of 1yr as allowed value
      this._maxDate = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate(), now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
    }

    this._isMobile = this._platformDetection.isMobile || ClientIdHelper.isMobile(this.props.clientId);

    this._datePickerStrings = {
      months: months,
      shortMonths: shortMonths,
      days: days,
      shortDays: shortDays,
      goToToday: stringsResx.goToToday,
      setExpirationDate: stringsResx.setExpirationDate,
      nextMonthAriaLabel: stringsResx.nextMonthAriaLabel,
      prevMonthAriaLabel: stringsResx.prevMonthAriaLabel,
      ariaLabel: this._formattedExpirationDateAriaLabel(),
      pickerAriaLabel: stringsResx.expirationDatePickerAriaLabel,
      closeButtonAriaLabel: this._isMobile && stringsResx.calendarCloseButton || ''
    };
  }

  public render(): React.ReactElement<{}> {
    let value = this.props.value;
    const hasExpired = value && this.state.hasExpired;
    this._datePickerStrings.ariaLabel = this._formattedExpirationDateAriaLabel();
    return (
      <div className={ 'od-ExpirationDatePicker' + (hasExpired ? ' od-ExpirationDatePicker-expired' : '') }>
        <div className={ 'od-ExpirationDatePicker-label' + (hasExpired ? ' od-ExpirationDatePicker-expired' : '') }>
          <Icon iconName={ hasExpired ? 'EventDateMissed12' : 'Calendar' } />
        </div>
        { this._renderPremiumGleam() }
        <div className={ 'od-ExpirationDatePicker-content' + (hasExpired ? ' od-ExpirationDatePicker-content-expired' : '') }>
          { this._renderExpiresLabel() }
          <DatePicker
            componentRef={ this._resolveRef('_datePicker') }
            strings={ this._datePickerStrings }
            placeholder={ this._datePickerStrings.setExpirationDate }
            isMonthPickerVisible={ false }
            onSelectDate={ this._onSelectDateForExpirationPicker }
            value={ value }
            formatDate={ this._formatDate }
            minDate={ this._minDate }
            maxDate={ this._maxDate }
            allowTextInput={ this._platformDetection.isIE }
            ariaLabel={ this._datePickerStrings.ariaLabel }
            pickerAriaLabel={ this._datePickerStrings.pickerAriaLabel }
            showGoToToday={ false }
            showCloseButton={ this._isMobile }
            allFocusable={ this._platformDetection.isIOS }
            calloutProps={ { preventDismissOnScroll: this._isMobile } }
            disableAutoFocus={ this.props.disableAutoFocus }
            tabIndex={ this.props.tabIndex }
          />
          { this._renderRemoveDateButton() }
        </div>
      </div>
    );
  }

  private _renderExpiresLabel() {
    // If expiration date has value
    if (this.props.value) {
      // Adds padding in IE 11 to account for rendering differences
      const expiresLabelClasses: string = 'od-ExpirationDate-expiresLabel' + (this._platformDetection.isIE ? ' od-ExpirationDate-expiresLabelIE' : '');
      return (
        <span className={ expiresLabelClasses } role='alert' aria-live='assertive'>
          { this.state.hasExpired ? stringsResx.expired : stringsResx.expires }
        </span>
      );
    }
  }

  private _renderRemoveDateButton() {
    const { selectedDate } = this.state;
    if (this.props.hideRemoveDateButton) {
      return;
    } else if (this.props.expiryRestriction > 0) {
      const tooltipContent = StringHelper.format(stringsResx.expirationDateToolTip, this.props.expiryRestriction);
      return (
        <div className='od-ExpirationDatePicker-info'>
          <TooltipHost
            id={ 'od-ExpirationDatePicker-tooltip-id' }
            hostClassName='od-ExpirationDatePicker-tooltip'
            content={ tooltipContent }
            directionalHint={ DirectionalHint.topCenter }
            calloutProps={ { gapSpace: 0 } }
            tooltipProps={ { maxWidth: '300px' } }
          >
            <div
              tabIndex={ 0 }
              className='od-ExpirationDatePicker-tooltipWrapper'
              aria-labeledby={ 'od-ExpirationDatePicker-tooltip-id' }
              aria-describedby={ 'od-ExpirationDatePicker-tooltip-id' }
              role='note'
            >
              <Icon iconName={ 'Info' } className={ 'ms-Icon ms-Icon--Info' } />
            </div>
          </TooltipHost>
        </div>
      );
    } else {
      return (
        <TooltipHost content={ stringsResx.removeExpirationLabel } id={ 'expirationDateRemovedButton' } calloutProps={ { gapSpace: 0 } }>
          <button
            className='od-ExpirationDatePicker-delete'
            onClick={ this._onDeleteDate }
            aria-labelledby={ 'expirationDateRemovedButton' }
            disabled={ !selectedDate }
          >
            <Icon iconName='Cancel' />
          </button>
          <span className='od-ExpirationDatePicker-hidden' role='alert' aria-live='assertive' aria-label={ this._expirationDateRemoved ? stringsResx.expirationDateRemoved : '' }>{ this._expirationDateRemoved ? stringsResx.expirationDateRemoved : '' }</span>
        </TooltipHost>
      );
    }
  }

  private _formattedExpirationDateAriaLabel() {
    const { selectedDate } = this.state;
    return selectedDate ?
      StringHelper.format(stringsResx.expirationDateValueAriaLabel, '') : // Added empty string as reader reads subsequent value.
      ''; // Screen reader reads the placeholder here, so no need to add an additional label
  }

  private _renderPremiumGleam() {
    if (this.props.showGleam) {
      return <Icon role='presentation' iconName='StarburstSolid' className='od-ExpirationDatePicker-premiumStarburst' />;
    } else {
      return;
    }
  }

  /**
   * Since a date has been selected, mark the boolean flag as true
   * @param date
   */
  @autobind
  private _onSelectDateForExpirationPicker(date: Date): void {
    this._expirationDateRemoved = false;
    const isExpired = date < new Date(Date.now());
    if (!this._platformDetection.isIE) {
      this._datePicker.focus();
    }

    this.setState({
      hasExpired: isExpired,
      selectedDate: date
    });

    this.props.onSelectDate(date);
  }

  @autobind
  private _onDeleteDate(): void {
    this._expirationDateRemoved = true;
    this.setState({
      selectedDate: null,
    }, () => {
      this.props.onSelectDate(null);
    });
  }

  @autobind
  private _formatDate(selectedDate: Date): string {
    if (selectedDate) {
      const day = selectedDate.getDay();
      const month = selectedDate.getMonth();

      return `${this._datePickerStrings.days[day]} ${this._datePickerStrings.shortMonths[month]} ${selectedDate.getDate()} ${selectedDate.getFullYear()}`;
    }

    return '';
  }
}